import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import { setChangeSceneType, setRestrictedToken, setSceneConfig, setSceneHistory } from '../../actions/configurationActions';
import { setCurrentSceneWidgets } from '../../actions/playerActions';
import { CHANGE_SCENE_TYPES } from '../../util/constants';
import { ArrowBack } from '../QPlayerSVGElement';
import ControlButton from '../Controls/ControlButton';

interface PasswordRestrictionContainerProps {
	isMobile?: boolean;
	onSubmitClick?: (_token: string) => void;
	playerConfigs?: any
}

const Container = styled.div<PasswordRestrictionContainerProps>`
    height: 100vh;
    width: 100%;
    background-color: #000;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    > div {
        padding 24px;
    }
`;

const FieldContainer = styled.div<PasswordRestrictionContainerProps>`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	${({ isMobile }) =>
		isMobile &&
		css`
			flex-direction: column;
		`}
`;

const PasswordInputLabel = styled.label<PasswordRestrictionContainerProps>`
	margin-bottom: 24px;
	display: block;
	font-weight: 600;
	${({ isMobile }) =>
		isMobile
			? css`
					font-size: 28px;
					text-align: center;
				`
			: css`
					font-size: 30px;
				`}
`;

const PasswordInput = styled.input<PasswordRestrictionContainerProps>`
	height: 42px;
	background: #ffffff;
	border: 1px solid #cdd4d9;
	border-radius: 4px;
	padding: 12px 24px;
	${({ isMobile }) =>
		!isMobile
			? css`
					width: 380px;
					margin-right: 1em;
				`
			: css`
					width: 100%;
					margin-bottom: 24px;
				`}
`;

const LoginButton = styled.button<PasswordRestrictionContainerProps>`
	cursor: pointer;
	border: none;
	background: #126cfc;
	box-shadow: 0px 3px 3px #00000060;
	border-radius: 4px;
	height: 42px;
	color: #fff;
	padding: 12px 24px;
	font-weight: 600;

	&:hover {
		background: #2152d0;
	}
`;

const ErrorMessage = styled.div`
	color: #f44352;
	font-size: 12px;
	margin-bottom: 0.5em;
	font-weight: 600;
`;

const PasswordRestrictionContainer: React.FC<PasswordRestrictionContainerProps> = ({
	isMobile,
	onSubmitClick: onSubmitClickProps,
	playerConfigs
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { scenePath, accountId, sceneConfig, currentMediaId } = useSelector((state) => (state as any).configurationReducer);

	const clickedSubmit = useRef(false);
	const [password, setPassword] = useState('');
	const [currentUserIP, setCurrentUserIP] = useState('');

	const getCurrentUserIP = async () => {
		const res = await axios.get('https://api.ipify.org/?format=json');
		setCurrentUserIP(res.data.ip);
	};

	const onPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(event.target.value);
	}, []);

	const onSubmitClick = useCallback(
		(_: React.MouseEvent<HTMLButtonElement>) => {
			const expirationDate = new Date(+new Date() + 300000).toISOString();
			const unsignedToken = expirationDate + '|' + currentUserIP + '|' + accountId;
			const token = (window as any).MD5.hash(unsignedToken, password) + '.' + (window as any).MD5.toHex(unsignedToken);
			setTimeout(() => clickedSubmit.current = true, 300);
			dispatch(
				setRestrictedToken({
					mediaId: currentMediaId,
					token: token,
				})
			);
			onSubmitClickProps?.(token);
		},
		[currentUserIP, accountId, password, dispatch, currentMediaId, onSubmitClickProps]
	);

	const onKeydown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				onSubmitClick(event as any);
			}
		},
		[onSubmitClick]
	);

	const onBackButtonClick = useCallback(() => {
		if (!scenePath || scenePath.length === 0) {
			return;
		}

		const lastPath = scenePath.pop();
		dispatch(setSceneConfig({ ...sceneConfig, currentScene: lastPath }));
		dispatch(setSceneHistory(scenePath));
		dispatch(setCurrentSceneWidgets(null));
		dispatch(setChangeSceneType(CHANGE_SCENE_TYPES.onclick));
	}, [dispatch, scenePath, sceneConfig]);

	useEffect(() => {
		getCurrentUserIP();
	}, []);

	const color = playerConfigs?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	return (
		<Container isMobile={isMobile}>
			<div>
				{scenePath && scenePath.length > 0 && (
					<ControlButton
						style={isMobile
							? { position: 'absolute', top: '12px', left: '12px', width: "32px", height: "32px", margin: "0" }
							: { position: 'absolute', top: '24px', left: '24px', margin: "0" }}
						hoverStyle={hoverStyle}
						tabIndex={0}
						ariaLabel={t('LABEL_GO_BACK_BTN')}
						onClick={onBackButtonClick}
						icon={<ArrowBack width={isMobile ? 18 : undefined} height={isMobile ? 15 : undefined}/>}
					/>
				)}

				<PasswordInputLabel isMobile={isMobile}>{t('LABEL_PASSWORD_MESSAGE')}</PasswordInputLabel>

				{clickedSubmit.current && (
					<ErrorMessage>
						<span>{t('LABEL_PASSWORD_ERROR')}</span>
					</ErrorMessage>
				)}

				<FieldContainer isMobile={isMobile}>
					<PasswordInput
						isMobile={isMobile}
						type="password"
						placeholder={t('LABEL_PASSWORD')}
						value={password}
						onChange={onPasswordChange}
						onKeyDown={onKeydown}
					/>
					<LoginButton isMobile={isMobile} onClick={onSubmitClick}>
						{t('LABEL_SUBMIT')}
					</LoginButton>
				</FieldContainer>
			</div>
		</Container>
	);
};

export default PasswordRestrictionContainer;
